import { ComponentRef, signal } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';

import { NbGlobalPosition, NbPositionHelper } from '@nebular/theme';
import { finalize, takeUntil, timer } from 'rxjs';
import { tap } from 'rxjs/operators';

import { BaseToastComponent } from '../../toast/base-toast.component';
import { ToastContainerComponent } from '../../toast-container/toast-container.component';
import { TInsertedToast, TToast } from '../toast.types';

import { ToastRef } from './toast-ref.class';

let toastCounter = 0;

export class ToastContainer<Component extends BaseToastComponent = BaseToastComponent> {
  get nativeElement() {
    return this.containerRef.location.nativeElement;
  }

  protected toasts = signal<TInsertedToast[]>([]);

  protected prevToast: TInsertedToast | null = null;

  constructor(
    public containerRef: ComponentRef<ToastContainerComponent>,
    protected position: NbGlobalPosition,
    protected positionHelper: NbPositionHelper,
  ) {}

  attach(data: TToast) {
    const toast: TInsertedToast = data as unknown as TInsertedToast;

    toast.id = this.getToastId();

    const duplicate = this.isDuplicate(toast);

    if (toast.config.preventDuplicates && duplicate) {
      return new ToastRef(this, duplicate);
    }

    this.removeToastIfLimitReached(toast);
    const toastComponent: Component = this.attachToast(toast);

    if (toastComponent) {
      toast.instance = toastComponent;
    }

    if (toast.config.destroyByClick) {
      this.subscribeOnClick(toastComponent, toast);
    }

    if (toast.config.duration) {
      this.subscribeOnDuration(toast);
    }

    this.prevToast = toast;

    return new ToastRef(this, toast);
  }

  destroy(toast: TInsertedToast) {
    if (this.prevToast?.id === toast.id) {
      this.prevToast = null;
    }

    this.toasts.update((list) => {
      return list.filter((t) => t.id !== toast.id);
    });
    this.updateContainer();

    return Promise.resolve();
  }

  protected isDuplicate(toast: TInsertedToast): TInsertedToast | null {
    return toast.config.duplicatesBehaviour === 'previous'
      ? this.isDuplicatePrevious(toast)
      : this.isDuplicateAmongAll(toast);
  }

  protected isDuplicatePrevious(toast: TInsertedToast): TInsertedToast | null {
    return this.prevToast! && this.toastDuplicateCompareFunc(this.prevToast, toast) ? this.prevToast : null;
  }

  protected isDuplicateAmongAll(toast: TInsertedToast): TInsertedToast | null {
    return this.toasts().find((t) => this.toastDuplicateCompareFunc(t, toast)) || null;
  }

  protected toastDuplicateCompareFunc = (t1: TInsertedToast, t2: TInsertedToast): boolean => {
    return t1.message === t2.message && t1.title === t2.title && t1.config.status === t2.config.status;
  };

  protected removeToastIfLimitReached(toast: TToast) {
    if (!toast.config.limit || this.toasts().length < toast.config.limit) {
      return;
    }

    if (this.positionHelper.isTopPosition(toast.config.position)) {
      this.toasts.update((list) => {
        list.pop();

        return [...list];
      });
    } else {
      this.toasts.update((list) => {
        list.shift();

        return [...list];
      });
    }
  }

  protected attachToast(toast: TInsertedToast): Component {
    if (this.positionHelper.isTopPosition(toast.config.position)) {
      return this.attachToTop(toast);
    }

    return this.attachToBottom(toast);
  }

  protected attachToTop(toast: TInsertedToast): Component {
    this.toasts.update((list) => {
      return [toast, ...list];
    });
    this.updateContainer();

    return this.containerRef.instance.getToastInstance(toast.id) as Component;
  }

  protected attachToBottom(toast: TInsertedToast): Component {
    this.toasts.update((list) => {
      return [...list, toast];
    });
    this.updateContainer();

    return this.containerRef.instance.getToastInstance(toast.id) as Component;
  }

  protected subscribeOnDuration(toast: TInsertedToast) {
    timer(toast.config.duration)
      .pipe()
      .subscribe(() => this.destroy(toast));
  }

  protected subscribeOnClick(toastComponent: Component, toast: TInsertedToast) {
    outputToObservable(toastComponent.clickEvent)
      .pipe(
        tap(() => {
          if (toast.config.destroyByClick) {
            this.destroy(toast);
          }
        }),
        takeUntil(outputToObservable(toastComponent.destroyEvent)),
      )
      .subscribe();
  }

  protected updateContainer() {
    this.containerRef.instance.content.set(this.toasts());
    this.containerRef.instance.position.set(this.position);
    this.containerRef.changeDetectorRef.detectChanges();
  }

  protected getToastId() {
    toastCounter++;

    return `${new Date().getTime()}-${toastCounter}`;
  }
}
